import { toast } from "react-toastify";
import { normalizeError } from "./normalizeError";
import { presentError } from "./presentError";
import { logError } from "./logError";
import type { AppError } from "./types";
import type { ErrorContext } from "./presentError";



/**
 * Gets a legacy unique constraint message based on the page context.
 * @param page The page context.
 * @returns The legacy unique constraint message.
 */
function getLegacyUniqueConstraintMessage(page?: string): string {
  if (page === "add account") return "קיים כרטיס עם מספר חשבון זה";
  if (page === "add car") return "קיים רכב עם מספר חשבון זה";
  if (page === "add work") return "קיים כרטיס עבודה עם מספר חשבון זה";
  if (page === "add item") return "קיים פריט עם מספר חשבון זה";
  return "חשבון זה כבר קיים";
}



/**
 * Gets a toast error message based on the error and page context.
 * @param error The error to get the message from.
 * @param page The page context.
 * @returns The toast error message.
 */
export function getToastErrorMessage(error: unknown, page?: string): string {

  const appError = normalizeError(error);
  
  if (appError.code === "DATABASE_CONSTRAINT_VIOLATION") {
    return getLegacyUniqueConstraintMessage(page);
  }

  if (appError.code === "RESOURCE_NOT_FOUND" && appError.fieldErrors?.account_number) {
    return "מספר חשבון לקוח אינו קיים";
  }

  return appError.userMessage;
}



/**
 * Handles errors with a boundary, displaying them according to their type and context.
 * @param error The error to handle.
 * @param showBoundary A function to display the error boundary.
 * @param onHandled A function to call when the error is handled.
 * @param context The context in which the error occurred.
 * @returns 
 */
export function handleErrorWithBoundary(
  error: unknown,
  showBoundary: (error: unknown) => void,
  onHandled?: (appError: AppError) => void,
  context: ErrorContext = "mutation"
): void {
  
  const appError = normalizeError(error);

  logError(appError, "legacy-boundary-handler");
  const mode = presentError(appError, context);

  if (mode === "silent") {
    return;
  }

  if (mode === "boundary") {
    showBoundary(Object.assign(new Error(appError.userMessage), { appError }));
    return;
  }

  // If the error was handled by a custom handler, we don't need to do anything else.
  if (onHandled) {
    onHandled(appError);
    return;
  }

  if (mode === "page") {
    showBoundary(Object.assign(new Error(appError.userMessage), { appError }));
    return;
  }

  if (mode === "inline") {
    if (!appError.fieldErrors) {
      toast.error(appError.userMessage);
    }
    return;
  }

  if (mode === "toast") {
    toast.error(appError.userMessage);
  }

}
